/**
 * Route paths of the main views and the app-root redirect rules (N-281).
 *
 * The app root ('/') opens the last visited screen, so reopening the browser
 * brings the user back where they were. Nothing is stored after a logout, so
 * a login opens Farm View, the default view. Logo / Home open Farm View. Field View has its own route so
 * a reload keeps it. Field links posted in Monday comments open Farm View on
 * the Field details tab.
 *
 * Kept free of window/React so it can be unit tested with node --test. The
 * stored value is read by lastVisitedPathStore.js and passed in.
 */

const FIELD_VIEW_PATH = '/FieldView';
// For comparisons against location.pathname (never contains the hash).
const FARM_VIEW_PATH = '/AllFarmView';
// Link / redirect target, same form as the Farm View tab link.
const FARM_VIEW_LINK = '/AllFarmView#';
// Logo, Home and breadcrumbs open Farm View, the default view. Never '/': it
// reopens the last screen, so Home on Account would stay on Account.
const HOME_PATH = FARM_VIEW_LINK;

// Every logged-in screen the root may send the user back to. Matches the
// PrivateRoute paths in Main.jsx except '/' itself. Public pages (login,
// register, map, privacy, …) are never remembered.
const RESTORABLE_PATHS = [
  FIELD_VIEW_PATH,
  FARM_VIEW_PATH,
  '/Planning',
  '/Insights',
  '/Settings',
  '/Account',
  '/DistributionUnitsManagement',
  '/transmitter-test',
  '/FarmingUnitsManagement',
  '/CropManagement',
  '/RemoteConfigurations',
];

// Value of the pre-N-281 Farm/Field preference that meant Field View.
const LEGACY_FIELD_VIEW_MODE = 'Field';

// Mirrors clients-shared extractQueryFromLocation: search first, else a '?'
// inside the hash.
const extractQuery = ({ search, hash } = {}) => {
  if (search) return search;
  const hashParts = (hash || '').split('?');
  return hashParts.length > 1 ? '?' + hashParts[1] : '';
};

/**
 * The field a link points at (`?distributionId=…&farmingUnitId=…[&fieldId]`,
 * e.g. the links the backend posts in Monday comments), or null when the
 * location is not such a link. Both ids are required, the same gate as
 * Dashboard.processUrlParams.
 *
 * @param {{ search?: string, hash?: string }} location
 * @returns {{distributionId: string, farmingUnitId: string,
 *   fieldId: string|null, origin: string|null}|null}
 */
const getFarmViewDeepLink = (location) => {
  const params = new URLSearchParams(extractQuery(location || {}));
  const distributionId = params.get('distributionId');
  const farmingUnitId = params.get('farmingUnitId');
  if (!distributionId || !farmingUnitId) return null;
  return {
    distributionId,
    farmingUnitId,
    fieldId: params.get('fieldId') || null,
    origin: params.get('origin') || null,
  };
};

/**
 * The screen to remember for a pathname: its base path when it is a
 * logged-in screen, else null. Case-insensitive like the router, so
 * '/account' is remembered as '/Account'.
 *
 * @param {string|null|undefined} pathname
 * @returns {string|null}
 */
const getPathToRemember = (pathname) => {
  if (typeof pathname !== 'string') return null;
  const lower = pathname.toLowerCase();
  return (
    RESTORABLE_PATHS.find((path) => {
      const base = path.toLowerCase();
      return lower === base || lower.startsWith(base + '/');
    }) || null
  );
};

/**
 * Whether the stored view state must be cleared: only when the user goes from
 * logged in to logged out (logout or a forced sign-out), so the next login
 * opens Farm View. A page load that starts logged out is not a logout.
 *
 * @param {boolean|undefined} wasLoggedIn - previous render's value
 * @param {boolean} isLoggedIn
 * @returns {boolean}
 */
const shouldClearViewState = (wasLoggedIn, isLoggedIn) =>
  wasLoggedIn === true && isLoggedIn === false;

const toLink = (path) => (path === FARM_VIEW_PATH ? FARM_VIEW_LINK : path);

/**
 * Where the app root sends a logged-in user, in this order:
 * 1. a field link → Farm View, query and hash kept for Farm View to read;
 * 2. the last visited screen;
 * 3. the old Farm/Field preference, when it said Field View;
 * 4. Farm View.
 *
 * @param {{ search?: string, hash?: string }} location
 * @param {string|null} [lastVisitedPath] - from lastVisitedPathStore
 * @param {string|null} [legacyViewMode] - the old 'Farm' | 'Field' value
 * @returns {string}
 */
const getRootRedirectTarget = (location, lastVisitedPath, legacyViewMode) => {
  if (getFarmViewDeepLink(location)) {
    return FARM_VIEW_PATH + (location.search || '') + (location.hash || '');
  }
  const remembered = getPathToRemember(lastVisitedPath);
  if (remembered) return toLink(remembered);
  if (legacyViewMode === LEGACY_FIELD_VIEW_MODE) return FIELD_VIEW_PATH;
  return FARM_VIEW_LINK;
};

/**
 * The pathname the Header should highlight. '/' only redirects, so it shows
 * the tab of the screen it redirects to.
 *
 * @param {string} pathname
 * @param {string} rootTarget - getRootRedirectTarget's result
 * @returns {string}
 */
const getActivePathname = (pathname, rootTarget) => {
  if (pathname !== '/') return pathname;
  return rootTarget.split(/[?#]/)[0];
};

const isFieldViewActive = (pathname) => pathname === FIELD_VIEW_PATH;

const isFarmViewActive = (pathname) => pathname.startsWith(FARM_VIEW_PATH);

/**
 * Where a notification click takes the user (N-281). Like a global search
 * field result: Farm View keeps its screen and tab and only selects the field
 * (it holds the selection in component state, so it needs a focus request);
 * Field View follows the Redux selection; any other screen opens Field View.
 *
 * @param {string} pathname
 * @param {{fieldId: string, farmingUnitId: string}} ids
 * @returns {{path: string|null, focusField: object|null}}
 */
const getNotificationNavigationPlan = (
  pathname,
  { fieldId, farmingUnitId },
) => {
  if (isFarmViewActive(pathname)) {
    return {
      path: null,
      focusField: { fieldId, farmingUnitId, openDetails: false },
    };
  }
  if (isFieldViewActive(pathname)) return { path: null, focusField: null };
  return { path: FIELD_VIEW_PATH, focusField: null };
};

module.exports = {
  getNotificationNavigationPlan,
  FIELD_VIEW_PATH,
  FARM_VIEW_PATH,
  FARM_VIEW_LINK,
  HOME_PATH,
  RESTORABLE_PATHS,
  getFarmViewDeepLink,
  getPathToRemember,
  shouldClearViewState,
  getRootRedirectTarget,
  getActivePathname,
  isFieldViewActive,
  isFarmViewActive,
};
